import api from '../client';

/**
 * Service pour la gestion des agents IA
 */
const agentService = {
  /**
   * Récupérer la liste des agents
   * @param {Object} params - Paramètres (page, limit, status)
   * @returns {Promise} Liste des agents avec pagination
   */
  getAgents: async (params = {}) => {
    try {
      const response = await api.get('/agents', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Créer une session WhatsApp pour un agent et récupérer le QR code
   * @param {string} id - ID de l'agent
   * @returns {Promise} Agent mis à jour avec connections.whatsapp
   */
  connectWhatsApp: async (id) => {
    try {
      const response = await api.post(`/agents/${id}/whatsapp/connect`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer un agent par son ID
   * @param {string} id - ID de l'agent
   * @returns {Promise} Données de l'agent
   */
  getAgentById: async (id) => {
    try {
      const response = await api.get(`/agents/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Créer un nouvel agent
   * @param {Object} agentData - Données de l'agent
   * @returns {Promise} Agent créé
   */
  createAgent: async (agentData) => {
    try {
      const response = await api.post('/agents', agentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Mettre à jour un agent
   * @param {string} id - ID de l'agent
   * @param {Object} agentData - Nouvelles données
   * @returns {Promise} Agent mis à jour
   */
  updateAgent: async (id, agentData) => {
    try {
      const response = await api.put(`/agents/${id}`, agentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Supprimer un agent
   * @param {string} id - ID de l'agent
   * @returns {Promise} Confirmation de suppression
   */
  deleteAgent: async (id) => {
    try {
      const response = await api.delete(`/agents/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Déployer un agent
   * @param {string} id - ID de l'agent
   * @returns {Promise} Agent déployé
   */
  deployAgent: async (id) => {
    try {
      const response = await api.post(`/agents/${id}/deploy`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Arrêter un agent
   * @param {string} id - ID de l'agent
   * @returns {Promise} Agent arrêté
   */
  stopAgent: async (id) => {
    try {
      const response = await api.post(`/agents/${id}/stop`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Tester un agent dans le Playground (éphémère — ne persiste rien).
   * Le backend assemble le system prompt (prompt + style + langue), fait du RAG
   * sur la base de connaissances de l'agent et injecte ses produits.
   * @param {string} id - ID de l'agent (doit être enregistré)
   * @param {Object} payload - { message, history?, overrides? }
   *   - message: string (requis)
   *   - history: [{ role: 'user'|'assistant', content }]
   *   - overrides: { prompt, model, temperature, maxTokens, style } (priment sur la config)
   * @returns {Promise} { success, data: { reply, usage, retrievedKnowledge, usedProducts } }
   */
  testAgent: async (id, payload) => {
    try {
      const response = await api.post(`/agents/${id}/test`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer les statistiques d'un agent
   * @param {string} id - ID de l'agent
   * @returns {Promise} Statistiques de l'agent
   */
  getAgentStats: async (id) => {
    try {
      const response = await api.get(`/agents/${id}/stats`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default agentService;
